import * as React from 'react';
import {
  Stack,
  Text,
  Checkbox,
  PrimaryButton,
  DefaultButton,
  MessageBar,
  MessageBarType,
  Spinner,
  SpinnerSize,
  Dialog,
  DialogType,
  DialogFooter,
  Icon,
  Modal,
  IconButton,
} from '@fluentui/react';

import type { IPBTCase } from '../../../Interfaces/IPBTCase';
import { PBTCaseStatus, PBTEngagementModel } from '../../../Interfaces/IPBTCase';
import {
  attestationPanelStyles,
  attestationPanelTextStyles,
  attestationPanelButtonStyles,
} from './AttestationPanel.styled';
import moment from 'moment';
import { AttestationPanelMessages as msg } from './AttestationPanel.messages';
import { useScrollToBottom } from './useScrollToBottom';
import { useAttestationPanel, AttestationMode } from './useAttestationPanel';
import { usePermission, useFeature, useRole } from '../../../Hooks/useFeature';

interface AttestationPanelProps {
  selectedCase: IPBTCase | null;
  blocked?: boolean;
  onSubmitted?: () => void;
  /**
   * Bottom action-bar handlers. Supplied by the parent (CaseDetail) which owns the
   * useCaseActions hook, the pause/cancel dialogs, and the reminder state, so the panel
   * reuses that single source of truth instead of duplicating it. When a handler is
   * omitted the corresponding button renders but stays inert (as before wiring).
   */
  onPauseCase?: () => void;
  onResumeCase?: () => void;
  onCancelCase?: () => void;
  onBypassManager?: () => void;
  /** Enablement flags mirrored from the parent (already include permission/status checks). */
  canPause?: boolean;
  canResume?: boolean;
  canCancel?: boolean;
  canBypass?: boolean;
}

export const AttestationPanel: React.FC<AttestationPanelProps> = ({
  selectedCase,
  blocked = false,
  onSubmitted,
  onPauseCase,
  onResumeCase,
  onCancelCase,
  onBypassManager,
  canPause,
  canResume,
  canCancel,
  canBypass,
}) => {
  const [attestMode, setAttestMode] = React.useState<AttestationMode>('none');
  const hasNotifiedSubmittedRef = React.useRef(false);
  const { scrollRef, onScroll, hasReachedBottom } = useScrollToBottom(attestMode);

  // Admin/superuser override: a user holding Attestation.Complete who is neither the
  // assigned manager nor HRC can still attest (bypass alias-based visibility) per policy.
  const canAttestOverride = usePermission('Attestation.Complete');
  const role = useRole();

  // Attest is a feature-gated workflow action, enabled (level 2) for Manager/HRC/HRCLeader/Admin,
  // so a Manager can attest their own case. The manager-hidden action is "Attest on Behalf of
  // Manager" (feature.workflow.attestOnBehalf, gated below), which Managers never receive.
  const attestFeature = useFeature('page.hrcDashboard/modal.caseDetail/tab.caseDetail.workflow/feature.workflow.attest');

  // Bottom action bar is feature-gated per role. Manager "My Cases" has these hidden
  // (level 0); HRC/HRCLeader/Admin have them enabled (level 2). The Send Reminder action is
  // intentionally not rendered here — it lives solely in the dedicated "Reminder Actions" card
  // in CaseDetail (single, contextually appropriate location) to avoid a duplicate control.
  const pauseFeature = useFeature('page.hrcDashboard/modal.caseDetail/tab.caseDetail.workflow/feature.workflow.pause');
  const cancelFeature = useFeature('page.hrcDashboard/modal.caseDetail/tab.caseDetail.workflow/feature.workflow.cancel');
  const bypassFeature = useFeature('page.hrcDashboard/modal.caseDetail/tab.caseDetail.workflow/feature.workflow.bypass');

  // "Attest on Behalf of Manager" is an HRC/HRCLeader/Admin action. Managers must never see it
  // (they only attest their OWN case via feature.workflow.attest), so gate it on its own node.
  const attestOnBehalfFeature = useFeature('page.hrcDashboard/modal.caseDetail/tab.caseDetail.workflow/feature.workflow.attestOnBehalf');

  // Reset when case changes
  React.useEffect(() => {
    setAttestMode('none');
  }, [selectedCase?.id]);

  React.useEffect(() => {
    hasNotifiedSubmittedRef.current = false;
  }, [selectedCase?.id]);

  const {
    isChecked,
    isModalOpen,
    isSubmitting,
    isSubmitted,
    validationError,
    submitError,
    effectiveTermDate,
    lossOfAccessDate,
    is7DayState,
    attestActions,
    setChecked,
    handleSubmitClick,
    handleModalDismiss,
    handleConfirm,
  } = useAttestationPanel(selectedCase, attestMode, {
    attestFeatureEnabled: attestFeature.enabled,
    attestOnBehalfFeatureEnabled: attestOnBehalfFeature.enabled,
    canAttestOverride,
    role,
  });

  React.useEffect(() => {
    if (!isSubmitted || hasNotifiedSubmittedRef.current) {
      return;
    }

    hasNotifiedSubmittedRef.current = true;
    onSubmitted?.();
    if (typeof window !== 'undefined') {
      window.dispatchEvent(new CustomEvent('pbt:refresh-cases'));
    }
  }, [isSubmitted, onSubmitted]);

  // Keep showing the local success state even after the parent refresh updates the case status.
  if (isSubmitted) {
    return (
      <Stack tokens={{ childrenGap: 16 }} className={attestationPanelStyles.container}>
        <div className={attestationPanelStyles.successContainer} role="status" aria-live="polite">
          <Icon iconName="CompletedSolid" styles={attestationPanelTextStyles.successIcon} />
          <Text className={attestationPanelStyles.successTitle} block>
            {msg.successTitle}
          </Text>
          <Text styles={attestationPanelTextStyles.successSubtitle} block>
            {msg.successSubtitle}
          </Text>
          {effectiveTermDate && (
            <Text className={attestationPanelStyles.successDetail} block>
              <strong>{msg.effectiveTermDateLabel}</strong> {new Date(effectiveTermDate).toLocaleDateString()}
            </Text>
          )}
          {lossOfAccessDate && (
            <Text className={attestationPanelStyles.successDetail} block>
              <strong>{msg.lossOfAccessDateLabel}</strong> {new Date(lossOfAccessDate).toLocaleDateString()}
            </Text>
          )}
        </div>
      </Stack>
    );
  }

  if (!selectedCase) {
    return (
      <Stack tokens={{ childrenGap: 12 }} className={attestationPanelStyles.container}>
        <MessageBar messageBarType={MessageBarType.info}>{msg.selectCasePrompt}</MessageBar>
      </Stack>
    );
  }

  const isPaused = selectedCase.status === PBTCaseStatus.Paused;

  // Case-level action bar reuses the parent-owned action handlers and state.
  const actionBar = (
    <Stack horizontal tokens={{ childrenGap: 12 }} styles={{ root: { marginTop: 12 } }}>
      {pauseFeature.enabled && !isPaused && (
        <DefaultButton
          text={msg.pauseCaseBtn}
          iconProps={{ iconName: 'Pause' }}
          styles={attestationPanelButtonStyles.pauseCase}
          onClick={onPauseCase}
          disabled={canPause === false}
        />
      )}
      {pauseFeature.enabled && isPaused && (
        <DefaultButton
          text={msg.resumeCaseBtn}
          iconProps={{ iconName: 'Play' }}
          styles={attestationPanelButtonStyles.resumeCase}
          onClick={onResumeCase}
          disabled={canResume === false}
        />
      )}
      {cancelFeature.enabled && (
        <DefaultButton
          text={msg.cancelCaseBtn}
          iconProps={{ iconName: 'Cancel' }}
          styles={attestationPanelButtonStyles.cancelCase}
          onClick={onCancelCase}
          disabled={canCancel === false}
        />
      )}
      {bypassFeature.enabled && (
        <DefaultButton
          text={msg.bypassManagerBtn}
          iconProps={{ iconName: 'Switch' }}
          styles={attestationPanelButtonStyles.bypassManager}
          onClick={onBypassManager}
          disabled={canBypass === false}
        />
      )}
    </Stack>
  );

  if (selectedCase.status !== PBTCaseStatus.AwaitingAttestation) {
    return (
      <Stack tokens={{ childrenGap: 12 }} className={attestationPanelStyles.container}>
        <MessageBar messageBarType={MessageBarType.info}>
          {msg.attestationNotAvailable} <strong>{selectedCase.status}</strong>
        </MessageBar>
        {isPaused && actionBar}
      </Stack>
    );
  }

  const isPanelOpen = attestMode !== 'none';
  const modalTitle =
    attestMode === 'hrc'
      ? msg.modalTitleOnBehalf
      : selectedCase.engModel === PBTEngagementModel.ManagerHRCLed
        ? msg.completeAttestationBtn
        : msg.modalTitle;
  const hasValidTermConvDate =
    !!selectedCase.termConvDate && moment(selectedCase.termConvDate).isValid();
  const isFutureTermConvDate =
    hasValidTermConvDate && moment(selectedCase.termConvDate).isAfter(moment(), 'day');
  const isAttestationDisabled = blocked || !hasValidTermConvDate || isFutureTermConvDate;

  return (
    <Stack tokens={{ childrenGap: 16 }} className={attestationPanelStyles.container}>
      {/* Summary Card */}
      <div className={attestationPanelStyles.summaryCard}>
        <div className={attestationPanelStyles.summaryCardHeader}>
          <span className={attestationPanelStyles.summaryCardTitle}>{msg.attestationSubmissionTitle}</span>
          <span
            className={`${attestationPanelStyles.statusBadge} ${attestationPanelStyles.statusBadgeRight}`}
            role="status"
            aria-label={`Status: ${msg.statusAwaiting}`}
          >
            {msg.statusAwaiting}
          </span>
        </div>

        {/* Attest Buttons */}
        <div className={attestationPanelStyles.cardDivider}>
          <Stack tokens={{ childrenGap: 8 }}>
            {blocked && (
              <MessageBar messageBarType={MessageBarType.severeWarning} isMultiline>
                {role === 'Manager' ? msg.blockedManagerNote : msg.blockedByFlagNote}
              </MessageBar>
            )}
            {attestActions.map((action) => (
              <div key={action.key}>
                <PrimaryButton
                  text={action.text}
                  iconProps={{ iconName: action.iconName }}
                  onClick={() => setAttestMode(action.mode)}
                  disabled={isAttestationDisabled}
                  styles={{
                    ...attestationPanelButtonStyles.attestPrimary,
                    rootDisabled: { cursor: 'not-allowed' },
                  }}
                />
                <Text styles={attestationPanelTextStyles.buttonHintText}>{action.hint}</Text>
              </div>
            ))}
          </Stack>
        </div>
      </div>

      {/* Bottom Action Buttons (feature-gated: hidden for Manager "My Cases") */}
      {actionBar}

      {/* Attestation Form Modal (Centered Popup) */}
      <Modal
        isOpen={isPanelOpen}
        onDismiss={() => setAttestMode('none')}
        isBlocking={false}
        containerClassName={attestationPanelStyles.modalContainer}
        titleAriaId="attestation-modal-title"
      >
        <div className={attestationPanelStyles.modalHeader}>
          <Text id="attestation-modal-title" styles={attestationPanelTextStyles.modalHeaderTitle}>
            {modalTitle}
          </Text>
          <IconButton
            iconProps={{ iconName: 'Cancel' }}
            ariaLabel="Close"
            onClick={() => setAttestMode('none')}
            styles={attestationPanelTextStyles.closeButton}
          />
        </div>
        <div className={attestationPanelStyles.modalBody} ref={scrollRef} onScroll={onScroll}>
          <Stack tokens={{ childrenGap: 12 }}>
            {/* Instructions */}
            <Text styles={attestationPanelTextStyles.instructions} block>
              {msg.instructions} <b>{msg.instructionsBold}</b>
            </Text>

            {/* Employee Info */}
            <div className={attestationPanelStyles.employeeBar} role="region" aria-label="Employee information">
              <Stack tokens={{ childrenGap: 8 }}>
                <Text styles={attestationPanelTextStyles.employeeDetail}>
                  <strong>{msg.employeeAliasLabel}</strong> {selectedCase.empAlias ?? 'N/A'}
                </Text>
                <Text styles={attestationPanelTextStyles.employeeDetail}>
                  <strong>{msg.employeeNameLabel}</strong> {selectedCase.empName}
                </Text>
                <Text styles={attestationPanelTextStyles.employeeDetail}>
                  <strong>{msg.employeePernLabel}</strong> {selectedCase.pern}
                </Text>
              </Stack>
            </div>

            {/* Important Note */}
            <MessageBar messageBarType={MessageBarType.severeWarning} isMultiline>
              <strong>{msg.importantNoteTitle}</strong> {msg.importantNoteText}
            </MessageBar>

            {is7DayState && (
              <MessageBar messageBarType={MessageBarType.warning}>
                <strong>{msg.sevenDayStateWarning}</strong>
              </MessageBar>
            )}

            {/* Checkbox */}
            <div className={attestationPanelStyles.checkboxArea}>
              <Checkbox
                checked={isChecked}
                onChange={(_, checked) => setChecked(!!checked)}
                styles={{ label: { fontWeight: 700, fontSize: 13 } }}
                label={msg.checkboxLabel}
              />
            </div>

            {/* Errors */}
            {validationError && <MessageBar messageBarType={MessageBarType.error}>{validationError}</MessageBar>}
            {submitError && <MessageBar messageBarType={MessageBarType.error}>{submitError}</MessageBar>}

            {isSubmitting && <Spinner size={SpinnerSize.small} label={msg.submittingLabel} />}
          </Stack>
        </div>
        <div className={attestationPanelStyles.modalFooter}>
          <PrimaryButton
            text={msg.submitAttestationBtn}
            onClick={() => (!blocked ? handleSubmitClick() : undefined)}
            disabled={isSubmitting || !hasReachedBottom || blocked}
          />
          <DefaultButton text={msg.cancelBtn} onClick={() => setAttestMode('none')} disabled={isSubmitting} />
        </div>
      </Modal>

      {/* Confirmation Dialog */}
      <Dialog
        hidden={!isModalOpen}
        onDismiss={handleModalDismiss}
        dialogContentProps={{
          type: DialogType.normal,
          title: msg.confirmDialogTitle,
        }}
        modalProps={{ isBlocking: true, titleAriaId: 'confirm-dialog-title' }}
      >
        <Text styles={attestationPanelTextStyles.confirmDialogText}>{msg.confirmDialogText}</Text>
        <DialogFooter>
          <PrimaryButton text={msg.confirmBtn} onClick={() => (!blocked ? handleConfirm() : undefined)} disabled={blocked} />
          <DefaultButton text={msg.cancelBtn} onClick={handleModalDismiss} />
        </DialogFooter>
      </Dialog>
    </Stack>
  );
};
